import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { createHttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import { ApolloLink } from 'apollo-link';
import { config } from '../../config';
import { useAuth0 } from 'neon-auth0';
import { ApolloProvider } from '@apollo/react-hooks';
import { TrialState, useUser } from './UserProvider';
import {
    formatError,
    LoggerContextData,
    sessionId,
    useLogger,
} from './logger/Logger';
import { v4 as uuid } from 'uuid';
import {
    HttpLoggerOperationType,
    HttpLoggerRequest,
    HttpLoggerResponse,
    LogAction,
    LogLevel,
} from './logger/logger-types';
import moment from 'moment';
import { trialPeriodMsg } from '../../shared/error-handling';

interface AuthorizedApolloProviderProps {
    children: ReactNode;
}

export const AuthorizedApolloProvider: React.FC<
    AuthorizedApolloProviderProps
> = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();

    const { updateTrialState } = useUser();

    const log = useLogger();
    const logRef = useRef<LoggerContextData | undefined>();
    useEffect(() => {
        logRef.current = log;
    }, [log]);

    const addRequestIdLink = setContext(() => ({
        requestId: uuid(),
    }));

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        let trialErrorOccured = false;

        const requestId = operation.getContext().requestId;
        if (graphQLErrors) {
            graphQLErrors.forEach((error) => {
                const { message, locations, path, extensions } = error;
                if (extensions?.code)
                    error.message = extensions.code + '::' + error.message;

                if (!error.message.includes(trialPeriodMsg)) {
                    if (extensions && extensions.logAsWarning) {
                        logRef.current?.printLog(
                            LogLevel.WARN,
                            LogAction.None,
                            error.message,
                            {
                                message: error.message,
                                location: error.locations,
                                path: error.path,
                                extensions: error.extensions
                                    ? error.extensions.code
                                    : undefined,
                                requestId,
                            }
                        );
                    } else {
                        logRef.current?.error(
                            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                            undefined,
                            requestId
                        );
                    }
                } else {
                    trialErrorOccured = true;
                    updateTrialState(TrialState.TrialExpired);
                }
            });
        }

        if (!trialErrorOccured && networkError) {
            logRef.current?.error(
                'Network error',
                formatError(networkError),
                requestId
            );
            if ((networkError as any).error === 'login_required') {
                window.location.reload();
            }
        }
    });

    const authLink = setContext(async (_, { headers, requestId }) => {
        // calling getAccessTokenSilently will get us the token from the cache if we have it, or get a new one from Auth0
        // if it is expired. Search for expires in: https://auth0.com/blog/introducing-auth0-single-page-apps-spa-js-sdk/
        const token = await getAccessTokenSilently();

        // return the headers to the context so httpLink can read them
        return {
            headers: {
                ...headers,
                authorization: `Bearer ${token}`,
                requestid: requestId,
                sessionid: sessionId,
            },
        };
    });

    const logLink = new ApolloLink((operation, forward) => {
        const { operationName } = operation;
        if (!operationName) {
            console.error(
                'You provided graphQL query/mutation without operation name, please specify query/mutation operation name.'
            );
        }
        const requestId = operation.getContext().requestId;

        const requestLogData: HttpLoggerRequest = {
            operationName,
            timeStamp: moment().toISOString(),
            operationType: HttpLoggerOperationType.Request,
        };

        logRef.current?.printLog(
            LogLevel.INFO,
            LogAction.GraphQlRequest,
            HttpLoggerOperationType.Request,
            requestLogData,
            requestId
        );
        return forward(operation).map((response) => {
            const timeStamp = moment().toISOString();
            const responseLogData: HttpLoggerResponse = {
                operationName,
                timeStamp,
                operationType: HttpLoggerOperationType.Response,
                operationTimeMs: moment(timeStamp).diff(
                    requestLogData.timeStamp
                ),
            };
            logRef.current?.printLog(
                LogLevel.INFO,
                LogAction.GraphQlRequest,
                HttpLoggerOperationType.Response,
                responseLogData,
                requestId
            );
            return response;
        });
    });

    const httpLink = createHttpLink({
        uri: config.serverUrl + '/graphql',
        credentials: 'same-origin',
    });

    const [client] = useState(
        () =>
            new ApolloClient({
                link: ApolloLink.from([
                    addRequestIdLink,
                    errorLink,
                    authLink,
                    logLink,
                    httpLink,
                ]),
                cache: new InMemoryCache(),
            })
    );

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
